unit uLockerControls;
(*
  PROMPT LOCKERS -- Control Wrappers
  ====================================
  CREATED: 5/7/26
  UPDATED: 5/16/26
    - TLockerCommaTextMemo.GetText/SetText now use TCSVList for correct
      comma-delimited handling with StrictDelimiter -- no space splitting.

  UPDATED: 5/7/26
    - Added TLockerCommaTextMemo: overrides GetText/SetText to go through
      Lines.CommaText so comma-delimited fields (e.g. Tags) work as plain
      text in the pipeline with no special casing needed.
    - Removed RTTI from base class GetText/SetText -- pas2js does not support
      it reliably. Each subclass now overrides GetText/SetText directly against
      its typed FControl reference.

  Polymorphic wrappers for the UI controls used in pipelines.

  Each subclass holds its own typed control reference and overrides GetText/SetText
  directly -- no RTTI. GetControl is still used for Enabled/Visible/Show/Hide.

  Subclasses:
    TLockerEdit             -- TWebEdit
    TLockerMemo             -- TWebMemo       (adds Lines property)
    TLockerCommaTextMemo    -- TWebMemo       (Text goes through TCSVList)
    TLockerCombo            -- TWebComboBox   (adds Items, SetItems)
    TLockerLabel            -- TWebLabel
    TLockerCheckBox         -- TWebCheckBox   (Checked: Boolean, no Text)
    TLockerRadioGroup       -- TWebRadioGroup (ItemIndex: Integer, GetText read-only)
    TLockerPropertyInspector-- TWebStringGrid as name/value inspector

  Common interface (TLockerControl):
    Text     -- get/set overridden per subclass
    Enabled  -- delegates to TControl.Enabled
    Visible  -- delegates to TControl.Visible
    Show     -- sets Visible := True
    Hide     -- sets Visible := False
    Clear    -- sets Text to ''
    IsEmpty  -- True if Trim(Text) = ''
    Snapshot -- saves current Text as clean baseline
    Changed  -- True if Text differs from last snapshot
*)

interface

uses
  System.SysUtils, System.Classes, System.StrUtils, System.Math,
  WEBLib.StdCtrls, WEBLib.Grids, Vcl.StdCtrls, Vcl.Controls, WEBLib.JSON, JS, Web,
  uStrLists;

type

  // ── Base class ─────────────────────────────────────────────────────────────

  TLockerControl = class abstract
  private
    FSnapshot : string;
    function  GetEnabled: Boolean;
    procedure SetEnabled(aValue: Boolean);
    function  GetVisible: Boolean;
    procedure SetVisible(aValue: Boolean);
  protected
    function  GetControl: TControl; virtual; abstract;
    function  GetText: string; virtual;
    procedure SetText(const aValue: string); virtual;
  public
    constructor Create;

    property Text    : string  read GetText    write SetText;
    property Enabled : Boolean read GetEnabled write SetEnabled;
    property Visible : Boolean read GetVisible write SetVisible;

    procedure Show;     virtual;
    procedure Hide;     virtual;
    procedure Clear;    virtual;
    function  IsEmpty  : Boolean; virtual;
    procedure Snapshot; virtual;
    function  Changed  : Boolean; virtual;
  end;

  // ── TLockerEdit ────────────────────────────────────────────────────────────

  TLockerEdit = class(TLockerControl)
  private
    FControl : TEdit;
  protected
    function  GetControl: TControl; override;
    function  GetText: string; override;
    procedure SetText(const aValue: string); override;
  public
    constructor Create(aControl: TEdit);
  end;

  // ── TLockerMemo ────────────────────────────────────────────────────────────

  TLockerMemo = class(TLockerControl)
  protected
    FControl : TMemo;

    function  GetControl: TControl; override;
    function  GetText: string; override;
    procedure SetText(const aValue: string); override;
  public
    constructor Create(aControl: TMemo);
    function Lines: TStrings;
  end;

  // ── TLockerCommaTextMemo ───────────────────────────────────────────────────
  // Like TLockerMemo but Text reads/writes through TCSVList.
  // StrictDelimiter is set on TCSVList so spaces are never treated as delimiters.
  // Use for comma-delimited fields (Tags etc.) so the pipeline treats them
  // as plain strings with no special casing.

  TLockerCommaTextMemo = class(TLockerMemo)
  protected
    function  GetText: string; override;
    procedure SetText(const aValue: string); override;
  end;

  // ── TLockerCombo ───────────────────────────────────────────────────────────

  TLockerCombo = class(TLockerControl)
  private
    FControl : TComboBox;
  protected
    function  GetControl: TControl; override;
    function  GetText: string; override;
    procedure SetText(const aValue: string); override;
  public
    constructor Create(aControl: TComboBox);
    function  Items: TStrings;
    procedure SetItems(aList: TStrings);
  end;

  // ── TLockerLabel ───────────────────────────────────────────────────────────

  TLockerLabel = class(TLockerControl)
  private
    FControl : TLabel;
  protected
    function  GetControl: TControl; override;
    function  GetText: string; override;
    procedure SetText(const aValue: string); override;
  public
    constructor Create(aControl: TLabel);
  end;

  // ── TLockerCheckBox ────────────────────────────────────────────────────────
  // Use Checked for Boolean access. No Text interface -- not meaningful here.

  TLockerCheckBox = class(TLockerControl)
  private
    FControl : TCheckBox;
    function  GetChecked: Boolean;
    procedure SetChecked(aValue: Boolean);
  protected
    function  GetControl: TControl; override;
  public
    constructor Create(aControl: TCheckBox);
    property Checked : Boolean read GetChecked write SetChecked;
  end;

  // ── TLockerRadioGroup ──────────────────────────────────────────────────────
  // GetText returns the selected item's caption (read-only).
  // Use ItemIndex to set the selection directly.

  TLockerRadioGroup = class(TLockerControl)
  private
    FControl : TRadioGroup;
    function  GetItemIndex: integer;
    procedure SetItemIndex(aValue: integer);
  protected
    function  GetControl: TControl; override;
    function  GetText: string; override;
  public
    constructor Create(aControl: TRadioGroup);
    property ItemIndex : integer read GetItemIndex write SetItemIndex;
  end;

  // ── TLockerPropertyInspector ───────────────────────────────────────────────
  // Wraps a TWebStringGrid configured as a two-column name/value inspector.
  // Col 0 = Name (read-only), Col 1 = Value (read/write).
  // Interface mirrors TStrings for familiarity.

  TLockerPropertyInspector = class
  private
    FGrid : TStringGrid;
    function  GetName(aIndex: integer): string;
    function  GetValue(const aName: string): string;
    procedure SetValue(const aName: string; const aValue: string);
    function  GetValueFromIndex(aIndex: integer): string;
    procedure SetValueFromIndex(aIndex: integer; const aValue: string);
    function  GetRowCount: integer;
    procedure SetName(aIndex: integer; const Value: string);
  public
    constructor Create(aGrid: TStringGrid);

    procedure AddPair(const aName, aValue: string);
    procedure Clear;

    property Names[aIndex: integer]          : string  read GetName           write SetName;
    property Values[const aName: string]     : string  read GetValue          write SetValue;
    property ValueFromIndex[aIndex: integer] : string  read GetValueFromIndex write SetValueFromIndex;
    property RowCount                        : integer read GetRowCount;

    procedure Show;
    procedure Hide;
  end;

implementation

{ TLockerControl }

constructor TLockerControl.Create;
begin
  FSnapshot := '';
end;

function  TLockerControl.GetEnabled: Boolean;           begin Result := GetControl.Enabled end;
function  TLockerControl.GetText: string;               begin Result := '???'; end;
procedure TLockerControl.SetText(const aValue: string); begin end;
procedure TLockerControl.SetEnabled(aValue: Boolean);   begin GetControl.Enabled := aValue end;

function  TLockerControl.GetVisible: Boolean;         begin Result := GetControl.Visible end;
procedure TLockerControl.SetVisible(aValue: Boolean); begin GetControl.Visible := aValue end;

procedure TLockerControl.Show;             begin GetControl.Visible := True end;
procedure TLockerControl.Hide;             begin GetControl.Visible := False end;
procedure TLockerControl.Clear;            begin SetText('') end;
function  TLockerControl.IsEmpty: Boolean; begin Result := Trim(GetText) = '' end;
procedure TLockerControl.Snapshot;         begin FSnapshot := GetText end;
function  TLockerControl.Changed: Boolean; begin Result := GetText <> FSnapshot end;

{ TLockerEdit }

constructor TLockerEdit.Create(aControl: TEdit);
begin
  FControl := aControl;
  inherited Create;
end;

function  TLockerEdit.GetControl: TControl;          begin Result := FControl end;
function  TLockerEdit.GetText: string;               begin Result := FControl.Text end;
procedure TLockerEdit.SetText(const aValue: string); begin FControl.Text := aValue end;

{ TLockerMemo }

constructor TLockerMemo.Create(aControl: TMemo);
begin
  FControl := aControl;
  inherited Create;
end;

function  TLockerMemo.GetControl: TControl;          begin Result := FControl end;
function  TLockerMemo.GetText: string;               begin Result := FControl.Text end;
procedure TLockerMemo.SetText(const aValue: string); begin FControl.Text := aValue end;
function  TLockerMemo.Lines: TStrings;               begin Result := FControl.Lines end;

{ TLockerCommaTextMemo }

function TLockerCommaTextMemo.GetText: string;
var
//  i   : integer;
//  res : string;
  jsl : TJAStrList;
  s : string;
begin
{
  res := '';
  for i := 0 to FControl.Lines.Count-1 do
  begin
    if (res <> '') then res := res + ',';
    res := res + '"' + Trim(FControl.Lines[i]) + '"';
  end;
  Result := res; }

  jsl := TJAStrList.Create;
  jsl.AssignTStrings(FControl.Lines);

  s := FControl.Lines.Text;
  s := jsl.Text;

  Result := jsl.AsJSONArr.ToString;

//console.log('>>> TLockerCommaTextMemo.GetText from FControl.Lines.Text: {'+FControl.Lines.Text+'}');   // $%^ ###

//console.log('>>> TLockerCommaTextMemo.GetText from jsl.Text: {'+jsl.Text+'}');   // $%^ ###
end;


procedure TLockerCommaTextMemo.SetText(const aValue: string);
var
  jal : TJAStrList;
  arr : TJSONArray;
begin
  FControl.Lines.Clear;
  if (Trim(aValue) = '') then Exit;
  jal := TJAStrList.Create;
  if StartsStr('[', Trim(aValue)) then
  begin
    arr := TJSONObject.ParseJSONValue(aValue) as TJSONArray;
    jal.AsJSONArr := arr;
  end
  else
    FControl.Lines.Text := aValue;  // fallback -- plain string goes in as-is
  jal.AssignToTStrings(FControl.Lines);
//  console.log('>>> TLockerCommaTextMemo.SetText: {'+aValue+'}');
end;

{ TLockerCombo }

constructor TLockerCombo.Create(aControl: TComboBox);
begin
  FControl := aControl;
  inherited Create;
end;

function  TLockerCombo.GetControl: TControl;          begin Result := FControl end;
function  TLockerCombo.GetText: string;               begin Result := FControl.Text end;
procedure TLockerCombo.SetText(const aValue: string); begin FControl.Text := aValue end;
function  TLockerCombo.Items: TStrings;               begin Result := FControl.Items end;
procedure TLockerCombo.SetItems(aList: TStrings);     begin FControl.Items.Assign(aList) end;

{ TLockerLabel }

constructor TLockerLabel.Create(aControl: TLabel);
begin
  FControl := aControl;
  inherited Create;
end;

function  TLockerLabel.GetControl: TControl;          begin Result := FControl end;
function  TLockerLabel.GetText: string;               begin Result := FControl.Caption end;
procedure TLockerLabel.SetText(const aValue: string); begin FControl.Caption := aValue end;

{ TLockerCheckBox }

constructor TLockerCheckBox.Create(aControl: TCheckBox);
begin
  FControl := aControl;
  inherited Create;
end;

function  TLockerCheckBox.GetControl: TControl;          begin Result := FControl end;
function  TLockerCheckBox.GetChecked: Boolean;           begin Result := FControl.Checked end;
procedure TLockerCheckBox.SetChecked(aValue: Boolean);   begin FControl.Checked := aValue end;

{ TLockerRadioGroup }

constructor TLockerRadioGroup.Create(aControl: TRadioGroup);
begin
  FControl := aControl;
  inherited Create;
end;

function  TLockerRadioGroup.GetControl: TControl;          begin Result := FControl end;
function  TLockerRadioGroup.GetItemIndex: integer;         begin Result := FControl.ItemIndex end;
procedure TLockerRadioGroup.SetItemIndex(aValue: integer); begin FControl.ItemIndex := aValue end;

function TLockerRadioGroup.GetText: string;
begin
  Result := '';
  if InRange(FControl.ItemIndex, 0, FControl.Items.Count-1) then
    Result := FControl.Items[FControl.ItemIndex];
end;

{ TLockerPropertyInspector }

constructor TLockerPropertyInspector.Create(aGrid: TStringGrid);
begin
  FGrid := aGrid;
//  Clear;
  FGrid.Cells[0, 0] := 'Name';
  FGrid.Cells[1, 0] := 'Value';
end;

procedure TLockerPropertyInspector.AddPair(const aName, aValue: string);
var r : integer;
begin
  r := FGrid.RowCount;
  FGrid.RowCount := r + 1;
  FGrid.Cells[0, r] := aName;
  FGrid.Cells[1, r] := aValue;
end;

procedure TLockerPropertyInspector.Clear;                 begin FGrid.RowCount := 1 end;
function  TLockerPropertyInspector.GetRowCount: integer;  begin Result := FGrid.RowCount end;
procedure TLockerPropertyInspector.Show;                  begin FGrid.Visible := True end;
procedure TLockerPropertyInspector.Hide;                  begin FGrid.Visible := False end;
function  TLockerPropertyInspector.GetName(aIndex: integer): string;
                                                          begin Result := FGrid.Cells[0, aIndex] end;
procedure TLockerPropertyInspector.SetName(aIndex: integer; const Value: string);
                                                          begin FGrid.Cells[0, aIndex]:=Value  end;

function TLockerPropertyInspector.GetValue(const aName: string): string;
var r : integer;
begin
  Result := '';
  for r := 1 to FGrid.RowCount-1 do
    if FGrid.Cells[0, r] = aName then
      Exit(FGrid.Cells[1, r]);
end;

procedure TLockerPropertyInspector.SetValue(const aName: string; const aValue: string);
var r : integer;
begin
  for r := 1 to FGrid.RowCount-1 do
    if FGrid.Cells[0, r] = aName then
    begin
      FGrid.Cells[1, r] := aValue;
      Exit;
    end;
end;

function TLockerPropertyInspector.GetValueFromIndex(aIndex: integer): string;
begin
  Result := FGrid.Cells[1, aIndex];
end;

procedure TLockerPropertyInspector.SetValueFromIndex(aIndex: integer; const aValue: string);
begin
  FGrid.Cells[1, aIndex] := aValue;
end;

end.
